import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { CategoryPage as CategoryPageType } from "@outschool/gql-backend-generated";
import {
  TutorMatchEnrollmentDataContext,
  TutorMatchEnrollmentDataType
} from "@outschool/ui-components-website";
import {
  Loading,
  TrackingPage,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { useNodeRef } from "@outschool/ui-utils";
import React, { memo } from "react";
import { useLocation } from "react-router";
import { useDebouncedCallback as useDebouncedCallbackVendor } from "use-debounce";

import { categoryPageUrl } from "../../../../shared/Routes";
import { useCategoryPageQuery } from "../../../hooks/useCategoryPageQuery";
import { useCuratedCategoryPageQuery } from "../../../hooks/useCuratedCategoryPageQuery";
import useTrackSearchFiltersApplied from "../../../hooks/useTrackSearchFiltersApplied";
import { useGetSeoBreadcrumbTrailScript } from "../../../lib/seo/hooks/useGetSeoBreadcrumbTrailScript";
import TutorMatchActivityEnrollPage from "../../../routes/LearningPlanner/enrollmentFlow/TutorMatchActivityEnrollPage";
import { EnrollmentFromStripePurchaseOrder } from "../../enrolling/EnrollmentMeetingPurchaseWithStripe";
import HeaderTags from "../../HeaderTags";
import SimpleLayout, { SimpleLayoutProps } from "../../layouts/SimpleLayout";
import CategoryPage from "../basic/CategoryPage";
import CuratedPageBody from "./CuratedPageBody";

const PageHeaderTags: React.FC<{
  category?: CategoryPageType;
  curatedCategoryResult?: ReturnType<typeof useCuratedCategoryPageQuery>;
}> = memo(({ category, curatedCategoryResult }) => {
  const seoBreadcrumbTrailScript = useGetSeoBreadcrumbTrailScript(category);

  const curatedCategory =
    curatedCategoryResult?.data?.contentfulCategoryPageCollection?.items[0];

  if (!category || !curatedCategory) {
    return null;
  }

  const description = documentToPlainTextString(
    curatedCategory?.heroDescription?.json
  );
  const curatedCategoryHeroTitle = curatedCategory?.heroTitle || undefined;
  const curatedCategoryHeroImage = curatedCategory?.heroImage?.url || undefined;

  return (
    <HeaderTags
      title={curatedCategoryHeroTitle}
      meta={[
        { property: "og:title", content: curatedCategoryHeroTitle },
        { name: "description", content: description },
        { property: "og:description", content: description },
        {
          property: "og:image",
          content: curatedCategoryHeroImage
        },
        {
          property: "twitter:image",
          content: curatedCategoryHeroImage
        },
        {
          name: "thumbnail",
          content: curatedCategoryHeroImage
        }
      ]}
      canonical={categoryPageUrl(category.slug, category.basePath)}
      script={seoBreadcrumbTrailScript}
    />
  );
});

export const PageLoading: React.FC<{ loading: boolean }> = memo(
  ({ loading }) => {
    if (loading) {
      return <Loading delay={0} />;
    }
    return null;
  }
);

export default function CuratedCategoryPage({
  categorySlug
}: {
  categorySlug: string;
}) {
  const { searchUid, trackSearchFiltersApplied } =
    useTrackSearchFiltersApplied();
  const fetchContentfulPreview = !!useLocation().query["preview"];
  const [tutorMatchActivityEnrollNode, tutorMatchActivityEnrollRef] =
    useNodeRef();

  const curatedCategoryPageQueryResult = useCuratedCategoryPageQuery({
    categorySlug,
    preview: fetchContentfulPreview
  });
  const taxonomyQueryResult = useCategoryPageQuery(categorySlug);

  const [tutorMatchEnrollmentData, setTutorMatchEnrollmentData] =
    React.useState<TutorMatchEnrollmentDataType | undefined>(undefined);

  const { trackTouch: trackTutorMatchEnroll } = useImpressionTracking({
    node: tutorMatchActivityEnrollNode,
    trackingLabel: "tutor_match_page_activity_enroll",
    sharedProperties: {
      activity_uid: tutorMatchEnrollmentData?.activity.uid,
      sectionUid: tutorMatchEnrollmentData?.section?.uid
    },
    uniqueId: `tutor_match_page_activity_enroll-${tutorMatchEnrollmentData?.activity.uid}`
  });

  const [tutorMatchActivityEnrollmentOnComplete] = useDebouncedCallbackVendor(
    async (enrollments: EnrollmentFromStripePurchaseOrder[]) => {
      if (tutorMatchEnrollmentData) {
        const enrollment = enrollments[0];
        if (enrollment) {
          // track learner planner enrollment
          trackTutorMatchEnroll({
            enrollmentUid: enrollment.uid,
            activityUid: tutorMatchEnrollmentData.activity.uid,
            sectionUid: enrollment.section_uid
          });
        }

        enrollment &&
          setTutorMatchEnrollmentData?.({
            ...tutorMatchEnrollmentData,
            section: { uid: enrollment?.section_uid! }
          });

        return Promise.resolve();
      }
    },
    500
  );
  const isLoading =
    curatedCategoryPageQueryResult.loading || taxonomyQueryResult.loading;

  const layoutProps: Partial<SimpleLayoutProps> = {
    allowCategoriesNavbar: true
  };
  if (isLoading) {
    return (
      <SimpleLayout hideReviewReminder {...layoutProps}>
        <PageHeaderTags
          category={taxonomyQueryResult.data?.categoryPage}
          curatedCategoryResult={curatedCategoryPageQueryResult}
        />
        <PageLoading loading={isLoading} />
      </SimpleLayout>
    );
  }

  const error =
    curatedCategoryPageQueryResult.error || taxonomyQueryResult.error;

  if (error) {
    OsPlatform.captureMessage(
      `ERROR: loading contentful category page: ${error.message}`
    );
    return <CategoryPage />;
  }

  // If we're going to fallback to the category page, it has to be rendered by itself
  if (
    curatedCategoryPageQueryResult.data &&
    !curatedCategoryPageQueryResult.data.contentfulCategoryPageCollection?.items
      ?.length
  ) {
    OsPlatform.captureMessage(
      `ERROR: Curated category page slug "${categorySlug}" not found in Contentful. Falling back to standard category page.`
    );

    return <CategoryPage />;
  }

  return (
    <SimpleLayout hideReviewReminder {...layoutProps}>
      <TutorMatchEnrollmentDataContext.Provider
        value={{
          tutorMatchEnrollmentData,
          setTutorMatchEnrollmentData
        }}
      >
        <PageHeaderTags
          category={taxonomyQueryResult.data?.categoryPage}
          curatedCategoryResult={curatedCategoryPageQueryResult}
        />
        <TrackingPage
          uniqueId={`curatedLandingPage-${categorySlug}`}
          name="curatedLandingPage"
        >
          {setImpressionNode => (
            <CuratedPageBody
              ref={node => node && setImpressionNode(node)}
              categorySlug={categorySlug}
              curatedCategoryPageQueryResult={curatedCategoryPageQueryResult}
              taxonomyQueryResult={taxonomyQueryResult}
              searchUid={searchUid}
              trackSearchFiltersApplied={trackSearchFiltersApplied}
              preview={fetchContentfulPreview}
            />
          )}
        </TrackingPage>
        {tutorMatchEnrollmentData && (
          <TutorMatchActivityEnrollPage
            ref={tutorMatchActivityEnrollRef}
            tutorMatchEnrollmentData={tutorMatchEnrollmentData}
            onCancel={() => {
              setTutorMatchEnrollmentData(undefined);
            }}
            onPaymentComplete={tutorMatchActivityEnrollmentOnComplete}
          />
        )}
      </TutorMatchEnrollmentDataContext.Provider>
    </SimpleLayout>
  );
}
